import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Header } from '../components/Header';
import { useT } from '../i18n';
import { OnboardingGuide } from '../components/OnboardingGuide';

interface Overview {
  platformOwner?: boolean;
  periodDays: number;
  totals: {
    sent: number; delivered: number; read: number; failed: number;
    repliers: number; humanReplies: number;
    contacts: number; contactsAdded: number; contactsFromWhatsApp: number; unsubscribed: number;
  };
  rates: { delivery: number; read: number; reply: number; failure: number; unsubscribe: number };
  trend: { sent: number | null; failed: number | null };
  inbox: { unread: number; awaitingReply: number; answered: number; medianResponseMinutes: number | null };
  daily: Array<{ day: string; sent: number; delivered: number; replies: number }>;
  connections: Array<{ name: string; status: string; inboxEnabled: boolean; sentToday: number; warmingUp?: boolean; dailyLimit?: number; daysSending?: number }>;
  campaigns: Array<{ id: string; name: string; status: string; sent: number; delivered: number; failed: number; deliveryRate: number; readRate: number }>;
}

const PERIODS = [7, 30, 90];

function authHeaders(): HeadersInit {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const formatNumber = (value: number) => value.toLocaleString('en-US');

function Kpi({ label, value, sub, trend, tone = 'text-gray-900' }: {
  label: string; value: string; sub?: string; trend?: number | null; tone?: string;
}) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${tone}`}>{value}</div>
      <div className="flex items-center gap-2 mt-1">
        {trend !== undefined && trend !== null && (
          <span className={`text-xs font-semibold ${trend >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
            {trend >= 0 ? '▲' : '▼'} {Math.abs(trend)}%
          </span>
        )}
        {sub && <span className="text-xs text-gray-500">{sub}</span>}
      </div>
    </div>
  );
}

export function DashboardPage() {
  const t = useT();
  const [data, setData] = useState<Overview | null>(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`/api/dashboard?days=${days}`, { headers: authHeaders() });
      if (!response.ok) throw new Error(String(response.status));
      setData(await response.json());
    } catch {
      toast.error(t('dash.errLoad'));
    } finally {
      setLoading(false);
    }
  }, [days, t]);

  useEffect(() => {
    load();
  }, [load]);

  const formatDuration = (minutes: number | null) => {
    if (minutes === null) return '—';
    if (minutes < 1) return t('dash.seconds', { count: Math.round(minutes * 60) });
    if (minutes < 60) return t('dash.minutes', { count: Math.round(minutes) });
    return t('dash.hours', { count: (minutes / 60).toFixed(1) });
  };

  // O dono da plataforma não tem empresa: a API responde só { platformOwner: true }, sem série diária
  const daily = data?.daily || [];
  const maxDaily = Math.max(...daily.map(d => Math.max(d.sent, d.replies)), 1);
  const hasActivity = daily.some(d => d.sent > 0 || d.replies > 0);

  return (
    <>
      <Header
        title={t('dash.title')}
        subtitle={t('dash.subtitle')}
        actions={
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
            {PERIODS.map(option => (
              <button
                key={option}
                onClick={() => setDays(option)}
                className={`px-3 py-1 text-xs rounded-md transition-colors ${
                  days === option ? 'bg-white shadow-sm font-semibold text-gray-900' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {t(`dash.period${option}`)}
              </button>
            ))}
          </div>
        }
      />

      <div className="p-6 space-y-6">
        <OnboardingGuide />
        {loading && !data && <div className="text-center text-gray-400 py-16">{t('common.loading')}</div>}

        {data?.platformOwner && (
          <div className="rounded-xl border border-gray-200 p-8 text-center text-gray-500">
            <p>{t('dash.ownerNote')}</p>
            <Link to="/superadmin/dashboard" className="btn-primary inline-block mt-4 text-sm">
              {t('dash.openPlatform')}
            </Link>
          </div>
        )}

        {data && !data.platformOwner && data.totals && (
          <div className={loading ? 'opacity-60 space-y-6' : 'space-y-6'}>
            {/* Indicadores */}
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
              <Kpi
                label={t('dash.sent')}
                value={formatNumber(data.totals.sent)}
                trend={data.trend.sent}
                sub={data.trend.sent === null ? t('dash.noBaseline') : t('dash.vsPrevious')}
              />
              <Kpi
                label={t('dash.delivery')}
                value={`${data.rates.delivery}%`}
                sub={t('dash.deliverySub', { delivered: formatNumber(data.totals.delivered), sent: formatNumber(data.totals.sent) })}
              />
              <Kpi
                label={t('dash.read')}
                value={`${data.rates.read}%`}
                sub={t('dash.readSub', { read: formatNumber(data.totals.read) })}
              />
              <Kpi
                label={t('dash.reply')}
                value={`${data.rates.reply}%`}
                tone="text-emerald-700"
                sub={t('dash.replySub', { people: formatNumber(data.totals.repliers), human: formatNumber(data.totals.humanReplies) })}
              />
              <Kpi
                label={t('dash.newContacts')}
                value={formatNumber(data.totals.contactsAdded)}
                sub={t('dash.newContactsSub', { total: formatNumber(data.totals.contacts) })}
              />
              <Kpi
                label={t('dash.unsubscribed')}
                value={formatNumber(data.totals.unsubscribed)}
                tone={data.totals.unsubscribed > 0 ? 'text-red-600' : 'text-gray-900'}
                sub={t('dash.unsubscribedSub', { rate: data.rates.unsubscribe })}
              />
            </div>

            {/* Atividade diária */}
            <div className="rounded-xl border border-gray-200 bg-white p-5">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <h3 className="font-semibold text-gray-900">{t('dash.activity')}</h3>
                <div className="flex items-center gap-4 text-xs text-gray-600">
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-slate-300 inline-block" />{t('dash.legendSent')}</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-blue-500 inline-block" />{t('dash.legendDelivered')}</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-500 inline-block" />{t('dash.legendReplies')}</span>
                </div>
              </div>

              {!hasActivity ? (
                <p className="text-center text-sm text-gray-400 py-10">{t('dash.noActivity')}</p>
              ) : (
                <div className="flex items-stretch gap-1 h-52" dir="ltr">
                  {daily.map((day, index) => {
                    // Com muitos dias, mostrar só alguns rótulos para não virar um borrão
                    const labelEvery = Math.ceil(daily.length / 12);
                    return (
                      <div key={day.day} className="flex-1 flex flex-col min-w-0 group relative">
                        {/* Coluna: enviadas (cinza) com a parte entregue preenchida, e respostas ao lado */}
                        <div className="flex-1 w-full flex items-end justify-center gap-[2px]">
                          <div className="w-1/2 bg-slate-200 rounded-t relative min-h-[2px]"
                               style={{ height: `${Math.max((day.sent / maxDaily) * 100, day.sent ? 2 : 0)}%` }}>
                            <div className="absolute bottom-0 left-0 right-0 bg-blue-500 rounded-t"
                                 style={{ height: day.sent ? `${(day.delivered / day.sent) * 100}%` : '0%' }} />
                          </div>
                          <div className="w-1/3 bg-emerald-500 rounded-t"
                               style={{ height: `${Math.max((day.replies / maxDaily) * 100, day.replies ? 2 : 0)}%` }} />
                        </div>

                        <span className="h-4 text-[9px] text-gray-400 truncate w-full text-center">
                          {index % labelEvery === 0 ? day.day.slice(5) : ''}
                        </span>

                        <div className="hidden group-hover:block absolute bottom-full mb-1 left-1/2 -translate-x-1/2 z-10 bg-gray-900 text-white text-[11px] rounded px-2 py-1 whitespace-nowrap">
                          {day.day} · {t('dash.legendSent')} {day.sent} · {t('dash.legendDelivered')} {day.delivered} · {t('dash.legendReplies')} {day.replies}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Caixa de entrada */}
              <div className="rounded-xl border border-gray-200 bg-white p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-900">{t('dash.inbox')}</h3>
                  <Link to="/inbox" className="text-sm text-emerald-700 hover:text-emerald-900">{t('dash.openInbox')} →</Link>
                </div>
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div className="rounded-lg bg-gray-50 p-3">
                    <div className="text-2xl font-bold text-gray-900">{formatNumber(data.inbox.unread)}</div>
                    <div className="text-xs text-gray-500 mt-1">{t('dash.unread')}</div>
                  </div>
                  <div className="rounded-lg bg-gray-50 p-3">
                    <div className={`text-2xl font-bold ${data.inbox.awaitingReply > 0 ? 'text-amber-600' : 'text-gray-900'}`}>
                      {formatNumber(data.inbox.awaitingReply)}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">{t('dash.awaiting')}</div>
                  </div>
                  <div className="rounded-lg bg-gray-50 p-3">
                    <div className="text-2xl font-bold text-gray-900">{formatDuration(data.inbox.medianResponseMinutes)}</div>
                    <div className="text-xs text-gray-500 mt-1">{t('dash.responseTime')}</div>
                  </div>
                </div>
                {data.inbox.answered > 0 && (
                  <p className="text-xs text-gray-400 mt-3">{t('dash.answered', { count: formatNumber(data.inbox.answered) })}</p>
                )}
              </div>

              {/* Números */}
              <div className="rounded-xl border border-gray-200 bg-white p-5">
                <h3 className="font-semibold text-gray-900 mb-4">{t('dash.numbers')}</h3>
                {(data.connections || []).length === 0 ? (
                  <div className="text-center py-6">
                    <p className="text-sm text-gray-400">{t('dash.noNumbers')}</p>
                    <Link to="/whatsapp" className="text-sm text-emerald-700 hover:text-emerald-900 mt-2 inline-block">
                      {t('dash.connectNumber')} →
                    </Link>
                  </div>
                ) : (
                <div className="space-y-2">
                  {(data.connections || []).map(connection => (
                    <div key={connection.name} className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2">
                      <div className="min-w-0">
                        <div className="font-medium text-sm text-gray-900 truncate" dir="auto">{connection.name}</div>
                        <div className="text-xs text-gray-500">
                          {connection.inboxEnabled ? t('dash.receiving') : t('dash.notReceiving')}
                        </div>
                        {connection.warmingUp && (
                          // O cliente precisa saber por que a campanha está devagar
                          <div className="text-xs text-amber-700 mt-1">
                            {t('dash.warmingUp', { day: (connection.daysSending ?? 0) + 1, limit: connection.dailyLimit ?? 0 })}
                          </div>
                        )}
                      </div>
                      <div className="text-end shrink-0">
                        <span className={`inline-block w-2 h-2 rounded-full me-1 ${connection.status === 'WORKING' ? 'bg-emerald-500' : 'bg-red-500'}`} />
                        <span className="text-xs text-gray-600">{t('dash.sentToday', { count: formatNumber(connection.sentToday) })}</span>
                      </div>
                    </div>
                  ))}
                </div>
                )}
              </div>
            </div>

            {/* Campanhas */}
            <div className="rounded-xl border border-gray-200 bg-white">
              <h3 className="font-semibold text-gray-900 p-4 border-b">{t('dash.campaigns')}</h3>
              {(data.campaigns || []).length === 0 ? (
                <p className="p-8 text-center text-sm text-gray-400">{t('dash.noCampaigns')}</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-start text-gray-500">
                      <tr>
                        <th className="text-start px-4 py-2">{t('dash.colCampaign')}</th>
                        <th className="text-start px-4 py-2">{t('dash.colSent')}</th>
                        <th className="text-start px-4 py-2">{t('dash.colDelivery')}</th>
                        <th className="text-start px-4 py-2">{t('dash.colRead')}</th>
                        <th className="text-start px-4 py-2">{t('dash.colFailed')}</th>
                        <th className="text-start px-4 py-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.campaigns || []).map(campaign => (
                        <tr key={campaign.id} className="border-t">
                          <td className="px-4 py-2 font-medium text-gray-900" dir="auto">{campaign.name}</td>
                          <td className="px-4 py-2">{formatNumber(campaign.sent)}</td>
                          <td className="px-4 py-2">{campaign.deliveryRate}%</td>
                          <td className="px-4 py-2">{campaign.readRate}%</td>
                          <td className={`px-4 py-2 ${campaign.failed > 0 ? 'text-red-600' : ''}`}>{formatNumber(campaign.failed)}</td>
                          <td className="px-4 py-2 text-end">
                            <Link to={`/campaigns?report=${campaign.id}`} className="text-emerald-700 hover:text-emerald-900 whitespace-nowrap">
                              {t('dash.viewAnalytics')} →
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default DashboardPage;
